<template>
    <section
        v-if="variant"
        class="overflow-hidden rounded-2xl border border-theme-border bg-theme-card"
        :data-variant="variant"
    >
        <div class="grid grid-cols-1 md:grid-cols-5">
            <div class="flex flex-col gap-5 p-5 md:col-span-3 md:p-6">
                <div class="flex flex-col gap-1">
                    <p
                        class="text-xs font-semibold uppercase tracking-wide text-theme-muted"
                    >
                        Tu plan
                    </p>
                    <h2
                        class="text-xl font-bold text-theme-base"
                        data-test="plan-card-title"
                    >
                        {{ title }}
                    </h2>

                    <template v-if="variant === 'trial'">
                        <p
                            class="text-base font-semibold text-theme-base"
                            data-test="plan-card-remaining"
                        >
                            {{ remainingText }}
                        </p>
                        <p v-if="endsAt" class="text-sm text-theme-muted">
                            Termina el {{ endsAt | dateSimple }}. Después tu
                            catálogo pasa a Free.
                        </p>
                        <p
                            v-if="showLosses"
                            class="text-sm text-theme-base"
                            data-test="plan-card-losses"
                        >
                            En Free {{ lossesText }} se guardan, pero dejan de
                            estar incluidos.
                        </p>
                    </template>

                    <p
                        v-else-if="variant === 'free-downgraded'"
                        class="text-sm text-theme-muted"
                        data-test="plan-card-downgraded"
                    >
                        Tus productos siguen guardados y 3 quedan publicados:
                        elige cuáles desde Productos.
                    </p>

                    <p
                        v-if="
                            variant === 'free' || variant === 'free-downgraded'
                        "
                        class="text-sm text-theme-muted"
                        data-test="plan-card-free-limits"
                    >
                        En Free tienes hasta 5 enlaces y 3 productos. Con
                        Starter llegas a 20 enlaces y 24 productos, sin marca de
                        agua.
                    </p>
                </div>

                <plan-benefits-list
                    :plan-code="benefitsPlanCode"
                    :title="benefitsTitle"
                />

                <div>
                    <app-button
                        color="brand-blue"
                        data-test="plan-card-cta"
                        @click="$emit('see-plans', plansPath)"
                    >
                        Ver planes
                    </app-button>
                </div>
            </div>

            <div
                class="relative flex items-center justify-center overflow-hidden bg-brand-navy p-6 min-h-48 md:col-span-2"
                aria-hidden="true"
            >
                <span
                    class="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-brand-red opacity-90"
                ></span>
                <span
                    class="absolute -bottom-12 -left-8 w-32 h-32 rounded-full border-4 border-brand-red opacity-60"
                ></span>

                <div
                    class="relative flex flex-col gap-3 w-48 rounded-2xl bg-white p-3 shadow-lg"
                >
                    <div class="flex items-center gap-2">
                        <span class="w-6 h-6 rounded-full bg-brand-navy"></span>
                        <span
                            class="w-16 h-2 rounded-full bg-brand-navy bg-opacity-20"
                        ></span>
                    </div>
                    <div class="grid grid-cols-2 gap-2">
                        <span
                            v-for="tile in mockTiles"
                            :key="tile.key"
                            class="flex items-center justify-center h-14 rounded-lg bg-brand-navy bg-opacity-10 text-xl"
                        >
                            {{ tile.icon }}
                        </span>
                    </div>
                    <span class="w-full h-2 rounded-full bg-brand-red"></span>
                </div>

                <span
                    v-if="benefitsPlanName"
                    class="absolute bottom-4 right-4 rounded-full bg-brand-red px-3 py-1 text-xs font-semibold text-white shadow"
                >
                    Plan {{ benefitsPlanName }}
                </span>
            </div>
        </div>
    </section>
</template>

<script>
import {
    nextPlanCode,
    planBenefits,
    planNameFromCode
} from "@/common/fields/plan-features";

import PlanBenefitsList from "@/components/utils/PlanBenefitsList.vue";

const DAY_MS = 24 * 60 * 60 * 1000;

// Lo que se pierde se muestra desde el primer recordatorio (30 días antes):
// con meses de prueba por delante, el aviso no aporta.
const LOSSES_VISIBLE_DAYS = 30;

const MOCK_TILES_COUNT = 4;
const MOCK_FALLBACK_ICON = "🛍️";

// portal-sesion-y-candados-optimizacion: única tarjeta de mejora de plan del
// home (reemplaza a TrialStatusCard y ProductsDowngradedCard). Sale del
// resumen de suscripción de la sesión; sin precio ni botón de cierre.
export default {
    name: "PlanUpgradeCard",
    components: { PlanBenefitsList },
    props: {
        // Resumen de sesión: { type, planCode, currentPeriodEnd } o null.
        subscription: {
            type: Object,
            default: null
        },
        productsUnlocked: {
            type: Boolean,
            default: false
        },
        // [{ label, used }]: lo que no cabe en Free al terminar la prueba.
        losses: {
            type: Array,
            default: () => []
        }
    },
    emits: ["see-plans"],
    computed: {
        variant() {
            const type = this.subscription && this.subscription.type;
            if (type === "trial") return "trial";
            if (type === "free") {
                return this.productsUnlocked ? "free-downgraded" : "free";
            }
            return null;
        },
        planName() {
            return planNameFromCode(this.subscription.planCode);
        },
        benefitsPlanCode() {
            if (this.variant === "trial") return this.subscription.planCode;
            return nextPlanCode("free");
        },
        benefitsPlanName() {
            return this.benefitsPlanCode
                ? planNameFromCode(this.benefitsPlanCode)
                : "";
        },
        benefitsTitle() {
            if (!this.benefitsPlanName) return "";
            return this.variant === "trial"
                ? `Si sigues con ${this.benefitsPlanName}, mantienes:`
                : `Con ${this.benefitsPlanName} tienes:`;
        },
        title() {
            return this.variant === "trial"
                ? `Estás en prueba de ${this.planName}`
                : "Estás en plan Free";
        },
        plansPath() {
            return this.variant === "trial"
                ? `/app/plan/change?plan=${this.subscription.planCode}`
                : "/app/plan/change";
        },
        endsAt() {
            return this.subscription.currentPeriodEnd || null;
        },
        daysRemaining() {
            if (!this.endsAt) return 0;
            const remainingMs = new Date(this.endsAt).getTime() - Date.now();
            return Math.max(0, Math.ceil(remainingMs / DAY_MS));
        },
        remainingText() {
            if (this.daysRemaining <= 0) return "Tu prueba termina hoy";
            return this.daysRemaining === 1
                ? "Te queda 1 día"
                : `Te quedan ${this.daysRemaining} días`;
        },
        showLosses() {
            return (
                this.losses.length > 0 &&
                this.daysRemaining <= LOSSES_VISIBLE_DAYS
            );
        },
        lossesText() {
            const items = this.losses.map(
                (loss) => `tus ${loss.used} ${loss.label}`
            );
            if (items.length === 1) return items[0];
            return `${items.slice(0, -1).join(", ")} y ${
                items[items.length - 1]
            }`;
        },
        mockTiles() {
            const icons = planBenefits(this.benefitsPlanCode).map(
                (benefit) => benefit.icon
            );
            return Array.from({ length: MOCK_TILES_COUNT }, (_, index) => ({
                key: index,
                icon: icons[index] || MOCK_FALLBACK_ICON
            }));
        }
    }
};
</script>
